import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  MessageBar,
  MessageBarBody,
  OverlayDrawer,
  Spinner,
  Switch,
  Text
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { SearchBox } from '@fluentui/react';
import { useTranslation } from 'react-i18next';
import * as ComponentFunctions from './ConceptSponsorForm.Functions';
import { useConceptSponsorFormStyles } from './ConceptSponsorForm.Styles';

const ConceptSponsorFormComponent = (props: ComponentFunctions.Props) => {
  const { t } = useTranslation();
  const styles = useConceptSponsorFormStyles();

  const [searchText, setSearchText] = useState<string>('');
  const [infoMessage, setInfoMessage] = useState<string | undefined>();

  useEffect(() => {
    if (!props.isOpen) {
      return;
    }

    props.GetAllConcepts();
    props.GetAllConceptSponsors(props.sponsorId);
  }, [props.isOpen, props.GetAllConcepts, props.GetAllConceptSponsors, props.sponsorId]);

  useEffect(() => {
    if (!props.isOpen) {
      setSearchText('');
      setInfoMessage(undefined);
    }
  }, [props.isOpen]);

  const normalizedSearch = searchText.trim().toLowerCase();

  const associatedRows = useMemo(
    () =>
      props.rows.filter((row) => {
        if (!row.isAssociated) return false;
        if (!normalizedSearch) return true;
        return [row.conceptName, row.conceptReferenceId, row.conceptDescription]
          .join(' ')
          .toLowerCase()
          .includes(normalizedSearch);
      }),
    [props.rows, normalizedSearch]
  );

  const unassociatedRows = useMemo(
    () =>
      props.rows.filter((row) => {
        if (row.isAssociated) return false;
        if (!normalizedSearch) return true;
        return [row.conceptName, row.conceptReferenceId, row.conceptDescription]
          .join(' ')
          .toLowerCase()
          .includes(normalizedSearch);
      }),
    [props.rows, normalizedSearch]
  );

  const handleToggleConcept = (rowKey: string, checked: boolean) => {
    const row = props.rows.find((item) => item.rowKey === rowKey);

    if (!row) {
      return;
    }

    setInfoMessage(undefined);

    if (checked && !row.isAssociated) {
      props.AddConceptSponsor({
        conceptId: row.conceptId,
        sponsorId: props.sponsorId
      });
      return;
    }

    if (!checked && row.isAssociated && row.associatedConceptSponsorId) {
      props.DeleteConceptSponsor(row.associatedConceptSponsorId);
    }
  };

  const isLoading = props.isLoadingConcepts || props.isLoadingConceptSponsors;

  return (
    <OverlayDrawer
      open={props.isOpen}
      onOpenChange={(_, data) => {
        if (!data.open) {
          props.dismissPanel();
        }
      }}
      position="end"
      size="large"
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              icon={<Dismiss24Regular />}
              aria-label={t('conceptSponsorForm.actions.close', 'Cerrar')}
              onClick={props.dismissPanel}
            />
          }
        >
          {t('conceptSponsorForm.title', 'Seleccionar conceptos a asociar')}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody className={styles.body}>
        <SearchBox
          placeholder={t('conceptSponsorForm.search.placeholder', 'Buscar por nombre, referencia o descripción')}
          value={searchText}
          onChange={(_, value) => setSearchText(value || '')}
          onClear={() => setSearchText('')}
        />

        {props.errorMessage && (
          <MessageBar intent="error">
            <MessageBarBody>{props.errorMessage}</MessageBarBody>
          </MessageBar>
        )}

        {infoMessage && (
          <MessageBar intent="info">
            <MessageBarBody>{infoMessage}</MessageBarBody>
          </MessageBar>
        )}

        {isLoading ? (
          <Spinner label={t('conceptSponsorForm.loading', 'Cargando conceptos...')} />
        ) : (
          <div className={styles.listsWrapper}>

            {/* ── Conceptos asociados ── */}
            <div className={styles.section}>
              <Text className={styles.sectionTitle}>
                {t('conceptSponsorForm.section.associated', 'Conceptos asociados')}
                {' '}({associatedRows.length})
              </Text>
              <div className={styles.listContainer}>
                {associatedRows.length === 0 ? (
                  <Text className={styles.emptySection}>
                    {t('conceptSponsorForm.section.associatedEmpty', 'No hay conceptos asociados.')}
                  </Text>
                ) : (
                  associatedRows.map((row) => (
                    <div className={styles.listItem} key={row.rowKey}>
                      <Switch
                        checked={row.isAssociated}
                        onChange={(_, data) => handleToggleConcept(row.rowKey, Boolean(data.checked))}
                      />
                      <div className={styles.itemText}>
                        <Text weight="bold">{row.conceptName}</Text>
                        {row.conceptDescription && (
                          <Text className={styles.mutedText}>{row.conceptDescription}</Text>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* ── Conceptos sin asociar ── */}
            <div className={styles.sectionGrow}>
              <Text className={styles.sectionTitle}>
                {t('conceptSponsorForm.section.unassociated', 'Conceptos sin asociar')}
                {' '}({unassociatedRows.length})
              </Text>
              <div className={styles.listContainerGrow}>
                {unassociatedRows.length === 0 ? (
                  <Text className={styles.emptySection}>
                    {t('conceptSponsorForm.section.unassociatedEmpty', 'No hay conceptos sin asociar.')}
                  </Text>
                ) : (
                  unassociatedRows.map((row) => (
                    <div className={styles.listItem} key={row.rowKey}>
                      <Switch
                        checked={row.isAssociated}
                        onChange={(_, data) => handleToggleConcept(row.rowKey, Boolean(data.checked))}
                      />
                      <div className={styles.itemText}>
                        <Text weight="bold">{row.conceptName}</Text>
                        {row.conceptDescription && (
                          <Text className={styles.mutedText}>{row.conceptDescription}</Text>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

          </div>
        )}
      </DrawerBody>

      <DrawerFooter>
        <Button onClick={props.dismissPanel} appearance="secondary">
          {t('conceptSponsorForm.actions.cancel', 'Cancelar')}
        </Button>
        <Button onClick={props.dismissPanel} appearance="primary">
          {t('conceptSponsorForm.actions.done', 'Listo')}
        </Button>
      </DrawerFooter>
    </OverlayDrawer>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(ConceptSponsorFormComponent as any);
